import {z as zod} from "zod";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import {robustJsonResultClientFetch} from "@vesta/error-handling-remix/fetches/robustJsonResultClientFetch";
import {userDefinedObjectTemplateValidator, type UserDefinedObjectTemplate} from "@vesta/vos-types/modules/workspaceLevel/userDefinedObjects/domain/UserDefinedObjectTemplate";
import {userDefinedObjectValidator, type UserDefinedObject} from "@vesta/vos-types/modules/workspaceLevel/userDefinedObjects/domain/UserDefinedObject";
import {type UserDefinedObjectTemplateAdditionalColumn, UserDefinedObjectTemplateAdditionalColumnType as ColumnType, UserDefinedObjectTemplateAdditionalColumnSourceType as ColumnSource} from "@vesta/vos-types/modules/workspaceLevel/userDefinedObjects/domain/UserDefinedObjectTemplateAdditionalColumn";
import {workspaceCapabilitiesValidator, getWorkspaceCapabilityOwnedUdoTemplateIds, type WorkspaceCapability} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/workspaceCapabilities/domain/WorkspaceCapability";
import {WorkspaceCapabilityType} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/workspaceCapabilities/domain/WorkspaceCapabilityType";
import type {ComplexFilter} from "@vesta/vos-types/modules/workspaceLevel/domain/ComplexFilter";
import {workspaceValidator, type Workspace} from "@vesta/vos-types/modules/platformLevel/workspaces/domain/Workspace";
import {adminUserDefinedObjectQueryResultValidator, type AdminUserDefinedObjectQueryResult} from "@vesta/vos-types/modules/workspaceLevel/userDefinedObjects/domain/AdminUserDefinedObjectQuery";
import {formatUserDefinedObjectAdditionalColumnValue} from "../userDefinedObjectAdditionalColumnValues";

import {FilterOperation} from "@vesta/vos-types/modules/workspaceLevel/domain/FilterOperation";
import type {FilterConfiguration} from "~/components/ottopilotDataViewer/filterConfiguration";
import {DATA_VIEWER_NULL_SENTINEL} from "~/utilities/queries";

export function encodeViewerFilterValue(value: string | null): string {
    return value === null ? DATA_VIEWER_NULL_SENTINEL : JSON.stringify(value);
}
export function decodeViewerFilterValue(value: string): string | null {
    return value === DATA_VIEWER_NULL_SENTINEL ? null : zod.string().parse(JSON.parse(value));
}
const searchValidator = zod.object({
    workspaceId: uuidZodValidator.optional().catch(undefined),
    templateId: uuidZodValidator.optional().catch(undefined),
});
export type AdminUdoViewerSearch = zod.output<typeof searchValidator>;
export const parseAdminUdoViewerSearch = (search: Record<string, unknown>): AdminUdoViewerSearch => searchValidator.parse(search);

export type AdminUdoViewerOptions = {
    raw: boolean;
    tab: "browse" | "query";
    limit: number;
    offset: number;
    orderBy: Array<{column: string; direction: "asc" | "desc"}>;
    filterRules: FilterConfiguration["lineItems"];
    visibleFilters: string[];
};
export type AdminUdoViewerState = AdminUdoViewerSearch & AdminUdoViewerOptions;
export function createAdminUdoViewerOptions(): AdminUdoViewerOptions {
    return {raw: false, tab: "browse", limit: 50, offset: 0, orderBy: [{column: "updatedAt", direction: "desc"}], filterRules: [], visibleFilters: []};
}
export function resetAdminUdoViewerSelection(options: AdminUdoViewerOptions): AdminUdoViewerOptions {
    return {...options, offset: 0, orderBy: [{column: "updatedAt", direction: "desc"}], filterRules: [], visibleFilters: []};
}
export function selectViewerWorkspace(workspaceId?: Uuid): AdminUdoViewerSearch {
    return {workspaceId};
}
export function selectViewerTemplate(search: AdminUdoViewerSearch, templateId: Uuid): AdminUdoViewerSearch {
    return {workspaceId: search.workspaceId, templateId};
}

type ApiContext = {intellsysBaseUrl: string; accessToken: string};
export const adminUdoViewerQueryKey = (context: ApiContext, workspaceId?: Uuid) => ["admin-udo-data-viewer", context.intellsysBaseUrl, context.accessToken, workspaceId] as const;

export async function adminUdoViewerPost<T>(context: ApiContext, path: string, body: unknown, validator: zod.ZodType<T>): Promise<T> {
    const result = await robustJsonResultClientFetch(`${context.intellsysBaseUrl}/sub-system/pluto/admin/${path}`, {
        method: "POST",
        headers: {"Content-Type": "application/json", Authorization: `Bearer ${context.accessToken}`},
        body: JSON.stringify(body),
    }, validator);
    if (!result.success) {
        throw result.error;
    }
    return result.data;
}

export async function fetchViewerWorkspaces(context: ApiContext): Promise<Workspace[]> {
    const workspaces: Workspace[] = [];
    const limit = 1000;
    while (true) {
        const page = await adminUdoViewerPost(context, "workspaces/get", {limit, offset: workspaces.length, sort: {field: "displayName", direction: "asc"}}, zod.array(workspaceValidator));
        workspaces.push(...page);
        if (page.length < limit) {
            return workspaces;
        }
    }
}
export const fetchViewerTemplates = (context: ApiContext, workspaceId: Uuid) =>
    adminUdoViewerPost(context, "user-defined-objects/templates/get", {workspaceId, includeArchived: true}, zod.array(userDefinedObjectTemplateValidator));
export const fetchViewerCapabilities = (context: ApiContext, workspaceId: Uuid) =>
    adminUdoViewerPost(context, "workspace-capabilities/get", {workspaceId}, workspaceCapabilitiesValidator);

export function viewerFilters(search: Pick<AdminUdoViewerState, "filterRules">): ComplexFilter | undefined {
    if (search.filterRules.length === 0) {
        return undefined;
    }
    return {logic: "AND", filters: search.filterRules.map(rule => {
        if (rule.operation === FilterOperation.in) {
            if (rule.values.length === 0) {
                return {column: rule.columnId, operator: "in", value: []};
            }
            // Picker tokens preserve empty strings, literal "null", JSON text and exact decimals.
            return {logic: "OR" as const, filters: rule.values.map(token => ({column: rule.columnId, operator: "exactlyEqualsText", value: decodeViewerFilterValue(token)}))};
        }
        const operators: Record<Exclude<FilterOperation, FilterOperation.in>, string> = {
            [FilterOperation.contains]: "literalContains", [FilterOperation.notContains]: "notContains",
            [FilterOperation.equals]: "exactlyequals", [FilterOperation.notEquals]: "exactlynotequals",
            [FilterOperation.startsWith]: "startsWith", [FilterOperation.endsWith]: "endsWith",
            [FilterOperation.greaterThan]: "greaterThan", [FilterOperation.greaterThanEqualTo]: "greaterThanOrEqual",
            [FilterOperation.lessThan]: "lessThan", [FilterOperation.lessThanEqualTo]: "lessThanOrEqual",
            [FilterOperation.isNull]: "exactlyequals", [FilterOperation.isNotNull]: "exactlynotequals",
        };
        return {column: rule.columnId, operator: operators[rule.operation], value: rule.value};
    })};
}
export async function fetchViewerFilterValues(context: ApiContext, workspaceId: Uuid, templateId: Uuid, column: string, filterConfiguration: FilterConfiguration): Promise<string[]> {
    const values = await adminUdoViewerPost(context, "user-defined-objects/column-values", {
        workspaceId, templateId, column, filters: viewerFilters({filterRules: filterConfiguration.lineItems}),
    }, zod.array(zod.string().nullable()));
    return values.map(encodeViewerFilterValue);
}
export function viewerRowsRequest(search: AdminUdoViewerState) {
    return {workspaceId: search.workspaceId, templateId: search.templateId, includeDeleted: true,
        filters: viewerFilters(search), limit: search.limit, offset: search.offset,
        orderBy: search.orderBy};
}
export function viewerCountRequest(search: AdminUdoViewerState) {
    return {workspaceId: search.workspaceId, templateId: search.templateId, includeDeleted: true, filters: viewerFilters(search)};
}
export const fetchViewerRows = (context: ApiContext, search: AdminUdoViewerState) =>
    adminUdoViewerPost(context, "user-defined-objects/get", viewerRowsRequest(search), zod.array(userDefinedObjectValidator));
export const fetchViewerCount = (context: ApiContext, search: AdminUdoViewerState) =>
    adminUdoViewerPost(context, "user-defined-objects/count", viewerCountRequest(search), zod.number().int().min(0));

export function viewerOwners(capabilities: ReadonlyArray<Pick<WorkspaceCapability, "type" | "udoBindings">>): ReadonlyMap<string, string[]> {
    const owners = new Map<string, string[]>();
    for (const capability of capabilities) {
        const enumName = Object.entries(WorkspaceCapabilityType).find(([, value]) => value === capability.type)?.[0] ?? capability.type;
        const name = enumName.replace(/([a-z])([A-Z])/g, "$1 $2");
        for (const id of getWorkspaceCapabilityOwnedUdoTemplateIds([capability])) {
            owners.set(id, [...(owners.get(id) ?? []), name]);
        }
    }
    return owners;
}

export type ViewerReference = {kind: "user" | "role" | "ou" | "product" | "udo" | "template"; templateId?: Uuid};
export type ViewerFormattingContext = {
    referenceLabels?: ReadonlyMap<string, string>;
    resolveGeography?: (type: ColumnType, value: unknown) => string | null;
};
export type ViewerColumn = {key: string; label: string; additionalColumn?: UserDefinedObjectTemplateAdditionalColumn; reference?: ViewerReference};

export function viewerColumnReference(column: ViewerColumn): ViewerReference | undefined {
    const field = column.additionalColumn;
    if (!field) {
        return column.reference;
    }
    switch (field.type) {
        case ColumnType.user: return {kind: "user"};
        case ColumnType.userTemplate: return {kind: "role"};
        case ColumnType.product: return {kind: "product"};
        case ColumnType.ou:
        case ColumnType.ous: return {kind: "ou", templateId: field.configuration?.ouTemplateId};
        case ColumnType.userDefinedObject:
        case ColumnType.userDefinedObjects: return {kind: "udo", templateId: field.configuration.userDefinedObjectTemplateId};
        default: return undefined;
    }
}
export function viewerReferenceKey(reference: ViewerReference, id: string): string {
    return `${reference.kind}:${reference.templateId?.toLowerCase() ?? ""}:${id.toLowerCase()}`;
}
export function viewerColumns(template: UserDefinedObjectTemplate): ViewerColumn[] {
    return [
        {key: "id", label: "ID"},
        ...template.additionalColumns.map(additionalColumn => ({key: additionalColumn.id, label: additionalColumn.displayName, additionalColumn})),
        {key: "isDeleted", label: "Deleted"},
        {key: "createdAt", label: "Created at"},
        {key: "updatedAt", label: "Updated at"},
        {key: "createdBy", label: "Created by", reference: {kind: "user"}},
        {key: "updatedBy", label: "Updated by", reference: {kind: "user"}},
        {key: "revisionId", label: "Revision ID"},
    ];
}
/** Follow the shared Columns control's visibility/order, ignoring removed metadata fields. */
export function viewerVisibleColumns(columns: ReadonlyArray<ViewerColumn>, orderedVisibleColumnIds: ReadonlyArray<string> | undefined): ViewerColumn[] {
    if (orderedVisibleColumnIds === undefined) {
        return [...columns];
    }
    const byId = new Map(columns.map(column => [column.key, column]));
    return orderedVisibleColumnIds.flatMap(id => {
        const column = byId.get(id);
        return column ? [column] : [];
    });
}

export function viewerCellValue(row: UserDefinedObject, column: ViewerColumn): unknown {
    if (column.additionalColumn) {
        return row.additionalColumnValues[column.additionalColumn.id];
    }
    switch (column.key) {
        case "id": return row.id;
        case "isDeleted": return row.isDeleted;
        case "createdAt": return row.createdAt;
        case "updatedAt": return row.updatedAt;
        case "createdBy": return row.createdBy;
        case "updatedBy": return row.updatedBy;
        case "revisionId": return row.revisionId;
        case "templateId": return row.templateId;
        default: return undefined;
    }
}
export function rawViewerValue(value: unknown): string {
    if (value === undefined) {
        return "undefined";
    }
    return typeof value === "string" ? value : JSON.stringify(value);
}
export function formatViewerCell(value: unknown, column: ViewerColumn, raw: boolean, context: ViewerFormattingContext = {}): string {
    if (raw) {
        return rawViewerValue(value);
    }
    if (value === null || value === undefined) {
        return "—";
    }
    const reference = viewerColumnReference(column);
    if (reference && context.referenceLabels) {
        const values = Array.isArray(value) ? value : [value];
        return values.length === 0 ? "—" : values.map(id => {
            if (typeof id !== "string" || !uuidZodValidator.safeParse(id).success) {
                return rawViewerValue(id);
            }
            return context.referenceLabels?.get(viewerReferenceKey(reference, id)) ?? `${id} (unresolved)`;
        }).join(", ");
    }
    if (column.additionalColumn) {
        const formatted = formatUserDefinedObjectAdditionalColumnValue(column.additionalColumn, value, {
            resolveUser: id => id, resolveUserTemplate: id => id, resolveOrganizationalUnit: id => id, resolveProduct: id => id,
            resolveGeography: (type, geography) => {
                const label = context.resolveGeography?.(type, geography);
                if (label && label !== "-") {
                    return label;
                }
                const value = rawViewerValue(geography);
                return context.resolveGeography && uuidZodValidator.safeParse(geography).success ? `${value} (unresolved)` : value;
            },
        });
        // Until reference labels are loaded, show the actual stored IDs rather than question marks.
        if (column.additionalColumn.type === ColumnType.userDefinedObject || column.additionalColumn.type === ColumnType.userDefinedObjects) {
            return Array.isArray(value) ? value.join(", ") : rawViewerValue(value);
        }
        return formatted === "-" || formatted === "?" ? rawViewerValue(value) : formatted;
    }
    if (typeof value === "boolean") {
        return value ? "Yes" : "No";
    }
    return rawViewerValue(value);
}

export const executeViewerQuery = (context: ApiContext, workspaceId: Uuid, sql: string, limit: number) =>
    adminUdoViewerPost(context, "user-defined-objects/query", {workspaceId, sql, limit}, adminUserDefinedObjectQueryResultValidator);

export const viewerCoreSqlColumns = ["id", "revision_id", "is_deleted", "created_at", "created_by", "updated_at", "updated_by"] as const;
export const quoteViewerSqlIdentifier = (identifier: string): string => `"${identifier.replaceAll('"', '""')}"`;
export const viewerSqlTable = (workspaceId: Uuid, templateId: Uuid, logs = false): string =>
    `${quoteViewerSqlIdentifier(workspaceId)}.${quoteViewerSqlIdentifier(`${templateId}${logs ? "_logs" : ""}`)}`;

export function viewerPhysicalColumns(template: UserDefinedObjectTemplate): ViewerColumn[] {
    return [
        ...viewerCoreSqlColumns.map(key => ({key, label: key})),
        ...template.additionalColumns.filter(column => column.source.type === ColumnSource.physical)
            .map(additionalColumn => ({key: additionalColumn.id, label: additionalColumn.displayName, additionalColumn})),
    ];
}
export function viewerStarterSql(workspaceId: Uuid, template: UserDefinedObjectTemplate): string {
    const columns = viewerPhysicalColumns(template).map(column => {
        const identifier = `records.${quoteViewerSqlIdentifier(column.key)}`;
        return `    ${identifier}${column.additionalColumn ? ` AS ${quoteViewerSqlIdentifier(column.label)}` : ""}`;
    }).join(",\n");
    return `SELECT\n${columns}\nFROM ${viewerSqlTable(workspaceId, template.id)} AS records\nORDER BY records.updated_at DESC, records.id\nLIMIT 100;`;
}
export function viewerQueryColumns(result: AdminUserDefinedObjectQueryResult, templates: ReadonlyArray<UserDefinedObjectTemplate>): ViewerColumn[] {
    return result.columns.map((column, index) => ({
        key: String(index), // SQL permits duplicate aliases; column position is the identity.
        label: column.name,
        additionalColumn: column.udoTemplateId && column.udoAdditionalColumnId
            ? templates.find(template => template.id === column.udoTemplateId)?.additionalColumns.find(candidate => candidate.id === column.udoAdditionalColumnId)
            : undefined,
    }));
}
